///
/// EarthBrowser - Window Content Registry
///
/// Centralized registry for window content renderers
/// Allows the BottomDrawer to render content for any window type
/// Created by Matt Giger on December 28, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { ReactNode } from "react"
import { useEffect, useState } from "react"
import type { DatasetBrowserWindowState } from "@/redux/dialogSlice"

// Type for a content renderer function
export type WindowContentRenderer = (windowState: DatasetBrowserWindowState) => ReactNode

// Registry of content renderers by window ID
const contentRenderers = new Map<string, WindowContentRenderer>()

// Listeners for registry changes
const listeners = new Set<() => void>()

function notifyListeners() {
	listeners.forEach(listener => listener())
}

// Default renderer: shown briefly while a browser component mounts and
// registers its content (every fresh open docks into the drawer first)
const defaultRenderer: WindowContentRenderer = (windowState) => (
	<div className="flex flex-col items-center justify-center gap-3 h-full text-white/40">
		<div className="relative w-8 h-8">
			<div className="absolute inset-0 border-2 border-white/10 rounded-full" />
			<div className="absolute inset-0 border-2 border-transparent border-t-white/40 rounded-full animate-spin" />
		</div>
		<p className="text-sm">Loading {windowState.title}…</p>
	</div>
)

/**
 * Register a content renderer for a window
 */
export function registerWindowContent(windowId: string, renderer: WindowContentRenderer): void {
	contentRenderers.set(windowId, renderer)
	notifyListeners()
}

/**
 * Unregister a content renderer
 */
export function unregisterWindowContent(windowId: string): void {
	contentRenderers.delete(windowId)
	notifyListeners()
}

/**
 * Get content for a window
 */
export function getWindowContent(windowState: DatasetBrowserWindowState): ReactNode {
	const renderer = contentRenderers.get(windowState.id)
	if (renderer) {
		return renderer(windowState)
	}
	return defaultRenderer(windowState)
}

/**
 * Check if a window has registered content
 */
export function hasWindowContent(windowId: string): boolean {
	return contentRenderers.has(windowId)
}

/**
 * Get all registered window IDs
 */
export function getRegisteredWindowIds(): string[] {
	return Array.from(contentRenderers.keys())
}

/**
 * Hook to use window content - re-renders when registry changes
 */
export function useWindowContent(windowState: DatasetBrowserWindowState | null): ReactNode {
	const [, forceUpdate] = useState({})

	useEffect(() => {
		const listener = () => forceUpdate({})
		listeners.add(listener)
		return () => {
			listeners.delete(listener)
		}
	}, [])

	if (!windowState) return null
	return getWindowContent(windowState)
}

/**
 * Hook to register content for a window - automatically unregisters on unmount
 */
export function useRegisterWindowContent(
	windowId: string | undefined,
	renderer: WindowContentRenderer | undefined
): void {
	useEffect(() => {
		if (windowId && renderer) {
			registerWindowContent(windowId, renderer)
			return () => {
				unregisterWindowContent(windowId)
			}
		}
	}, [windowId, renderer])
}

